import type { Dimension, MatrixFor } from "@src/math/types";
import type {
  AnyEquation,
  Equation,
} from "@src/features/equation-editor/equation";
import type { PlaybackState } from "@src/features/playback/playback";
import { canVisualizeAnimation } from "../animation-eligibility";
import { createEquation } from "@src/features/equation-editor/equation-operations";
import { createIdlePlayback } from "@src/features/playback/playback";
import { identity } from "@src/math/matrix";

export interface AppState {
  activeDimension: Dimension;
  equations: { 2: Equation<2>; 3: Equation<3> };
  appliedTransforms: { 2: MatrixFor<2>; 3: MatrixFor<3> };
  animation: PlaybackState;
  showBasis: boolean;
  showGrid: boolean;
}

export type ActiveDimensionState =
  | {
      readonly dimension: 2;
      readonly equation: Equation<2>;
      readonly appliedTransform: AppState["appliedTransforms"][2];
    }
  | {
      readonly dimension: 3;
      readonly equation: Equation<3>;
      readonly appliedTransform: AppState["appliedTransforms"][3];
    };

export function createInitialAppState(createId?: () => string): AppState {
  return {
    activeDimension: 3,
    equations: {
      2: createEquation(2, createId),
      3: createEquation(3, createId),
    },
    appliedTransforms: {
      2: identity(2),
      3: identity(3),
    },
    animation: createIdlePlayback("steps"),
    showBasis: true,
    showGrid: true,
  };
}

export function selectActiveEquation(state: AppState): AnyEquation {
  return state.activeDimension === 2 ? state.equations[2] : state.equations[3];
}

export function selectActiveDimensionState(
  state: AppState,
): ActiveDimensionState {
  return state.activeDimension === 2
    ? {
        dimension: 2,
        equation: state.equations[2],
        appliedTransform: state.appliedTransforms[2],
      }
    : {
        dimension: 3,
        equation: state.equations[3],
        appliedTransform: state.appliedTransforms[3],
      };
}

export function selectCanPlay(state: AppState): boolean {
  const equation = selectActiveEquation(state);
  return (
    equation.evaluation.kind === "current" &&
    equation.matrices.length > 0 &&
    canVisualizeAnimation(equation.evaluation.value, state.animation.mode)
  );
}

export function selectCanShare(state: AppState): boolean {
  return (
    state.equations[2].evaluation.kind === "current" &&
    state.equations[3].evaluation.kind === "current"
  );
}
